import {
  BottomSheet,
  Divider,
  PageHeaderWrapper,
  Span,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { type AgentPreferences } from "@src/appV2/Agents/api/useWorkerPreferences/types";
import { type AgentLicenseData } from "@src/appV2/Agents/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Switch } from "@src/appV2/redesign/components/Switch";
import { useCallback } from "react";
import { useController, useForm } from "react-hook-form";

import { ShiftBlockDistanceField } from "./ShiftBlockDistanceField";
import { ShiftBlockQualificationFilter } from "./ShiftBlockQualificationFilter";

export interface ShiftBlockFilterFormValues extends AgentPreferences {
  enableDistanceFiltering: boolean;
}

interface ShiftBlockFilterBottomSheetProps {
  modalState: UseModalState;
  agentLicenses: AgentLicenseData[] | undefined;
  showDistanceFilter: boolean;
  defaultValues: ShiftBlockFilterFormValues;
  onSubmit: (values: ShiftBlockFilterFormValues) => void;
}

export function ShiftBlockFilterBottomSheet(props: ShiftBlockFilterBottomSheetProps) {
  const { modalState, agentLicenses, showDistanceFilter, defaultValues, onSubmit } = props;
  const { closeModal } = modalState;

  const { control, handleSubmit, formState, watch, reset } = useForm<ShiftBlockFilterFormValues>({
    defaultValues,
  });

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const distanceValue = watch("distance");

  const { field: enableDistanceFilteringField } = useController({
    name: "enableDistanceFiltering",
    control,
  });

  const { field: licenseField } = useController({
    name: "license",
    control,
  });

  const hasLicenses = isDefined(agentLicenses) && agentLicenses.length > 0;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter withShadow orientation="vertical" sx={{ gap: 7 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <IconButton
              size="large"
              variant="outlined"
              iconType="close"
              aria-label="Close filters"
              onClick={() => {
                resetForm();
                closeModal();
              }}
            />
            <Button
              variant="contained"
              size="large"
              disabled={formState.isSubmitting}
              onClick={handleSubmit((formData) => {
                onSubmit(formData);
                closeModal();
              })}
            >
              Apply
            </Button>
          </Stack>
        </DialogFooter>
      }
      onClose={resetForm}
    >
      <DialogContent sx={{ paddingBottom: 12 }}>
        <Stack spacing={8}>
          <PageHeaderWrapper variant="tertiary">
            <Title variant="h4" component="h4">
              Filters
            </Title>
          </PageHeaderWrapper>

          {hasLicenses && (
            <Stack spacing={5}>
              <Text semibold variant="body1">
                Qualification
              </Text>
              <ShiftBlockQualificationFilter
                value={licenseField.value}
                agentLicenses={agentLicenses}
                onChange={licenseField.onChange}
              />
            </Stack>
          )}

          {hasLicenses && showDistanceFilter && <Divider />}

          {showDistanceFilter && (
            <>
              <Stack spacing={5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Text semibold variant="body1">
                    Distance From Home
                  </Text>
                  <Text
                    variant="body2"
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.border?.subtle}`,
                      borderRadius: theme.borderRadius?.xSmall,
                      padding: 3,
                    })}
                  >
                    {enableDistanceFilteringField.value ? `${distanceValue} miles` : "Any distance"}
                  </Text>
                </Stack>
                <ShiftBlockDistanceField
                  name="distance"
                  disabled={!enableDistanceFilteringField.value}
                  control={control}
                />
              </Stack>

              <Divider />

              <Stack spacing={5} direction="row" justifyContent="space-between">
                <Stack spacing={2}>
                  <Text semibold variant="body1">
                    Any Distance
                  </Text>
                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                    Show shift blocks regardless of distance
                  </Text>
                </Stack>
                <Span sx={{ marginLeft: "auto" }}>
                  <Switch
                    aria-label="Show shift blocks at any distance"
                    checked={!enableDistanceFilteringField.value}
                    onChange={(event) => {
                      enableDistanceFilteringField.onChange(!event.target.checked);
                    }}
                  />
                </Span>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
